/**
 * Task #1527 — Banner sticky enterprise per quote R2/DB.
 * Promosso da `packages/frontend/src/components/layout/StorageQuotaBanner.tsx`.
 *
 * Escalation visiva per soglia (warning 85+, critical 95+, blocked 100+/105+),
 * dismiss persistito in sessionStorage per scope+bucket, CTA "Dettaglio quota"
 * + "Contatta supporto" da 100% in su. Navigazione disaccoppiata via prop
 * `onNavigateDetail` per riuso tenant + master.
 */
import { useEffect, useState } from 'react';
import { AlertTriangle, X, ExternalLink, LifeBuoy } from 'lucide-react';
import {
  useStorageQuota,
  shouldShowBanner,
  formatBytes,
  type StorageQuotaSnapshot,
  type QuotaStatus,
} from '../../hooks/useStorageQuota';

const SS_KEY = 'edg.storageBanner.dismissed.v2';

interface DismissState {
  r2?: { bucket: number; ts: number };
  db?: { bucket: number; ts: number };
}

function readDismiss(): DismissState {
  try {
    const raw = sessionStorage.getItem(SS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as DismissState;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch { return {}; }
}
function writeDismiss(s: DismissState): void {
  try { sessionStorage.setItem(SS_KEY, JSON.stringify(s)); } catch { /* private mode */ }
}

function bucketOf(pct: number): number {
  if (pct >= 105) return 105;
  if (pct >= 100) return 100;
  if (pct >= 95) return 95;
  if (pct >= 85) return 85;
  return 0;
}

function paletteFor(blocked: boolean, anyAt100: boolean): string {
  if (blocked) return 'bg-red-100 dark:bg-red-900/40 border-red-500 text-red-900 dark:text-red-100';
  if (anyAt100) return 'bg-orange-100 dark:bg-orange-900/30 border-orange-400 text-orange-900 dark:text-orange-100';
  return 'bg-amber-100 dark:bg-amber-900/30 border-amber-400 text-amber-900 dark:text-amber-100';
}

export interface StorageQuotaBannerProps {
  fetchSnapshot: () => Promise<StorageQuotaSnapshot | null>;
  /** Navigazione al dettaglio quota — il chiamante decide la rotta. */
  onNavigateDetail: () => void;
  /** Email/URL contatto supporto. Se null la CTA secondaria viene nascosta. */
  supportContactHref?: string;
}

export function StorageQuotaBanner(props: StorageQuotaBannerProps): JSX.Element | null {
  const { fetchSnapshot, onNavigateDetail, supportContactHref } = props;
  const { snapshot } = useStorageQuota({ fetchSnapshot });
  const [dismissed, setDismissed] = useState<DismissState>(() => readDismiss());

  useEffect(() => {
    if (!snapshot) return;
    const next: DismissState = { ...dismissed };
    let changed = false;
    if (next.r2 && bucketOf(snapshot.r2.pct) < next.r2.bucket) { delete next.r2; changed = true; }
    if (next.db && snapshot.db && bucketOf(snapshot.db.pct) < next.db.bucket) { delete next.db; changed = true; }
    if (changed) { writeDismiss(next); setDismissed(next); }
  }, [snapshot]); // eslint-disable-line react-hooks/exhaustive-deps -- pre-esistente senza motivazione, descrizione aggiunta meccanicamente (#3359)

  if (!shouldShowBanner(snapshot) || !snapshot) return null;

  const r2Status: QuotaStatus = snapshot.r2.status;
  const dbStatus: QuotaStatus = snapshot.db?.status ?? 'ok';
  const r2Blocked = r2Status === 'blocked';
  const dbBlocked = dbStatus === 'blocked';
  const anyBlocked = r2Blocked || dbBlocked;
  const anyAt100 = snapshot.r2.pct >= 100 || (snapshot.db?.pct ?? 0) >= 100;

  const r2Bucket = bucketOf(snapshot.r2.pct);
  const dbBucket = snapshot.db ? bucketOf(snapshot.db.pct) : 0;
  const showR2 = (r2Status !== 'ok' && r2Status !== 'info') &&
    (r2Blocked || !dismissed.r2 || dismissed.r2.bucket < r2Bucket);
  const showDb = !!snapshot.db && (dbStatus !== 'ok' && dbStatus !== 'info') &&
    (dbBlocked || !dismissed.db || dismissed.db.bucket < dbBucket);

  if (!showR2 && !showDb) return null;

  const messages: string[] = [];
  if (showR2) {
    if (r2Blocked) {
      messages.push(`Storage R2 a ${snapshot.r2.pct.toFixed(1)}% — upload bloccati (${formatBytes(snapshot.r2.usedBytes)} / ${formatBytes(snapshot.r2.maxBytes)})`);
    } else {
      messages.push(`Storage R2 a ${snapshot.r2.pct.toFixed(1)}% — vicino al limite`);
    }
  }
  if (showDb && snapshot.db) {
    if (dbBlocked) {
      messages.push(`Database a ${snapshot.db.pct.toFixed(1)}% — nuovi embedding sospesi`);
    } else {
      messages.push(`Database a ${snapshot.db.pct.toFixed(1)}% — vicino al limite`);
    }
  }
  if (snapshot.emergencyUnblockUntil) {
    messages.push(`Sblocco emergenza attivo fino ${new Date(snapshot.emergencyUnblockUntil).toLocaleString('it-IT')}`);
  }
  if (anyBlocked && snapshot.blockedCategories.length > 0) {
    messages.push(`Categorie bloccate: ${snapshot.blockedCategories.join(', ')}`);
  }

  const palette = paletteFor(anyBlocked, anyAt100);
  const showCtas = anyAt100 || anyBlocked;

  const dismissAll = (): void => {
    const next: DismissState = { ...dismissed };
    if (showR2 && !r2Blocked) next.r2 = { bucket: r2Bucket, ts: Date.now() };
    if (showDb && !dbBlocked && snapshot.db) next.db = { bucket: dbBucket, ts: Date.now() };
    writeDismiss(next);
    setDismissed(next);
  };

  const canDismiss = (showR2 && !r2Blocked) || (showDb && !dbBlocked);

  return (
    <div
      className={`sticky top-0 z-40 border-b ${palette} px-4 py-2 text-sm flex items-start gap-3`}
      role="alert"
      aria-live={anyBlocked ? 'assertive' : 'polite'}
    >
      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div>
          <span className="font-semibold mr-2">Quota storage:</span>
          {messages.join(' · ')}
        </div>
        {showCtas && (
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onNavigateDetail}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-white/80 hover:bg-white dark:bg-black/20 dark:hover:bg-black/30 text-xs font-medium border border-current/20"
            >
              Dettaglio quota <ExternalLink className="h-3 w-3" />
            </button>
            {supportContactHref && (
              <a
                href={supportContactHref}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-transparent hover:bg-white/40 dark:hover:bg-black/20 text-xs font-medium border border-current/30"
              >
                <LifeBuoy className="h-3 w-3" /> Contatta supporto
              </a>
            )}
          </div>
        )}
      </div>
      {canDismiss && (
        <button
          type="button"
          onClick={dismissAll}
          aria-label="Nascondi avviso quota"
          title="Nascondi avviso (riapparirà alla prossima soglia)"
          className="shrink-0 opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-current rounded p-0.5"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

export default StorageQuotaBanner;
